import React, { useState } from 'react';
import { Calculator, Info, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';

export default function BuoyComparator() {
  const buoys = {
    FLC1200: { area1: 0.87045, area2: 0.3436385, vol: 0.55, mass: 125 },
    FLC1500: { area1: 1.058417, area2: 0.459024, vol: 0.958, mass: 208 },
    FLC1800: { area1: 2.2692935, area2: 0.821214, vol: 1.577, mass: 446.8 },
    FLC1800HV: { area1: 3.2040535, area2: 0.821214, vol: 1.577, mass: 446.8 },
    FLC2200: { area1: 3.262058, area2: 1.092214, vol: 3.335, mass: 758 },
    FLC2200HV: { area1: 4.196758, area2: 1.092214, vol: 3.335, mass: 758 },
    FLC2600: { area1: 2.37947667, area2: 1.53894233, vol: 5.9, mass: 1814 },
    FLC3000: { area1: 3.0813055, area2: 1.5557285, vol: 8.17, mass: 2077 }
  };

  const chains = {
    ND16: { d: 16, m: 4.8, load: 81500, name: 'Long Link Chain 5D ND16 Q2' },
    ND18: { d: 18, m: 6, load: 106000, name: 'Long Link Chain 5D ND18 Q2' },
    ND20: { d: 20, m: 7.5, load: 134600, name: 'Long Link Chain 5D ND20 Q2' },
    ND22: { d: 22, m: 9, load: 154900, name: 'Long Link Chain 5D ND22 Q2' },
    ND25: { d: 25, m: 11.6, load: 200800, name: 'Long Link Chain 5D ND25 Q2' },
    ND28: { d: 28, m: 14.6, load: 250800, name: 'Long Link Chain 5D ND28 Q2' },
    ND30: { d: 30, m: 16.7, load: 288500, name: 'Long Link Chain 5D ND30 Q2' },
    ND32: { d: 32, m: 19, load: 328200, name: 'Long Link Chain 5D ND32 Q2' },
    ND35: { d: 35, m: 22.8, load: 392500, name: 'Long Link Chain 5D ND35 Q2' },
    ND38: { d: 38, m: 26.9, load: 462800, name: 'Long Link Chain 5D ND38 Q2' },
    ND40: { d: 40, m: 29.8, load: 514800, name: 'Long Link Chain 5D ND40 Q2' },
    ND45: { d: 45, m: 37.7, load: 660400, name: 'Long Link Chain 5D ND45 Q2' }
  };

  const compat = {
    FLC1200: ['ND16', 'ND18', 'ND20', 'ND22'],
    FLC1500: ['ND16', 'ND18', 'ND20', 'ND22', 'ND25'],
    FLC1800: ['ND20', 'ND22', 'ND25', 'ND28'],
    FLC1800HV: ['ND20', 'ND22', 'ND25', 'ND28'],
    FLC2200: ['ND25', 'ND28', 'ND30', 'ND32'],
    FLC2200HV: ['ND25', 'ND28', 'ND30', 'ND32'],
    FLC2600: ['ND30', 'ND32', 'ND35', 'ND38'],
    FLC3000: ['ND35', 'ND38', 'ND40', 'ND45']
  };

  const buoyDimensions = {
    FLC1200: [1200, 290, 435, 400, 1000],
    FLC1500: [1500, 355, 720, 400, 1000],
    FLC1800: [1800, 560, 521, 600, 1500],
    FLC1800HV: [1800, 560, 521, 1200, 1500],
    FLC2200: [2200, 555, 795, 600, 2000],
    FLC2200HV: [2200, 555, 795, 1200, 2000],
    FLC2600: [2600, 1500, 1000, 800, 2840],
    FLC3000: [3000, 1500, 1100, 800, 2840]
  };

  const [numBuoys, setNumBuoys] = useState(1);
  const [buoy1, setBuoy1] = useState('FLC1500');
  const [chain1, setChain1] = useState('ND20');
  const [buoy2, setBuoy2] = useState('FLC2200');
  const [chain2, setChain2] = useState('ND28');
  const [showBuoy1Details, setShowBuoy1Details] = useState(false);
  const [showChain1Details, setShowChain1Details] = useState(false);
  const [showBuoy2Details, setShowBuoy2Details] = useState(false);
  const [showChain2Details, setShowChain2Details] = useState(false);
  
  const [data1, setData1] = useState({
    mass: 208, vol: 0.958, area1: 1.058417, area2: 0.459024,
    diam: 20, chainMass: 7.5, proof: 134600
  });
  
  const [data2, setData2] = useState({
    mass: 758, vol: 3.335, area1: 3.262058, area2: 1.092214,
    diam: 28, chainMass: 14.6, proof: 250800
  });
  
  const [results1, setResults1] = useState(null);
  const [results2, setResults2] = useState(null);
  const [env, setEnv] = useState({
    depth: 20, tide: 3, wave: 3, period: 8, wind: 25, current: 1.5
  });

  const updateEnv = (k, v) => setEnv(e => ({ ...e, [k]: parseFloat(v) || 0 }));

  const changeBuoy1 = (b) => {
    setBuoy1(b);
    if (b && buoys[b]) {
      const info = buoys[b];
      setData1(d => ({ ...d, mass: info.mass, vol: info.vol, area1: info.area1, area2: info.area2 }));
      const ok = compat[b] || [];
      if (!ok.includes(chain1) && ok.length > 0) changeChain1(ok[0]);
    }
  };

  const changeChain1 = (c) => {
    setChain1(c);
    if (c && chains[c]) {
      const info = chains[c];
      setData1(d => ({ ...d, diam: info.d, chainMass: info.m, proof: info.load }));
    }
  };

  const changeBuoy2 = (b) => {
    setBuoy2(b);
    if (b && buoys[b]) {
      const info = buoys[b];
      setData2(d => ({ ...d, mass: info.mass, vol: info.vol, area1: info.area1, area2: info.area2 }));
      const ok = compat[b] || [];
      if (!ok.includes(chain2) && ok.length > 0) changeChain2(ok[0]);
    }
  };

  const changeChain2 = (c) => {
    setChain2(c);
    if (c && chains[c]) {
      const info = chains[c];
      setData2(d => ({ ...d, diam: info.d, chainMass: info.m, proof: info.load }));
    }
  };

  const calculate = (buoyKey, chainKey, data) => {
    const g = 9.81;
    const H = env.depth + env.tide + 0.5 * env.wave;
    const Hm = Math.max(env.depth - 0.5 * env.wave, env.depth * 0.8);
    const wc = 0.015 * env.wind;
    const vc = env.wave * Math.sqrt(g) / (2 * env.period);
    const tc = env.current + wc + vc;
    const Fw = 0.5 * 1.29 * env.wind * env.wind * data.area1 * 1.2;
    const Fb = 0.5 * 1025 * tc * tc * data.area2 * 1.2;
    const ca = H * 0.8 * 2.65 * data.diam / 1000;
    const Fch = 0.5 * 1025 * tc * tc * ca * 1.2;
    const T = Fw + Fb + Fch;
    const L = Math.sqrt(H * (H + 2 * T / (data.chainMass * g)));
    const st = Math.sqrt(Hm * (Hm + 2 * T / (data.chainMass * g)));
    const arg = Hm * data.chainMass * g / T + 1;
    let at = 0;
    if (arg > 1.001) at = 2 * T / (data.chainMass * g * g) * Math.acosh(arg);
    const R = Math.max(0, L - st + at);
    const rb = data.vol - (data.mass + data.chainMass * L) / 1025;
    const rp = (rb / data.vol) * 100;
    const req = 5 * (data.chainMass * g * H + T);
    const sinker = 1.5 * T * 2400 / (g * (2400 - 1025) * Math.tan(Math.PI / 4));
    
    return {
      buoyName: buoyKey,
      chainName: chains[chainKey] ? chains[chainKey].name : 'Custom',
      len: Math.round(L * 10) / 10,
      radius: Math.round(R * 10) / 10,
      resB: Math.round(rb * 1000) / 1000,
      resP: Math.round(rp * 10) / 10,
      sinker: Math.round(sinker),
      ok: data.proof >= req,
      req: Math.round(req / 1000),
      valid: rp >= 15,
      H: H,
      T: T,
      chainMass: data.chainMass
    };
  };

  const calcAll = () => {
    setResults1(calculate(buoy1, chain1, data1));
    if (numBuoys === 2) {
      setResults2(calculate(buoy2, chain2, data2));
    } else {
      setResults2(null);
    }
  };

  const generateCatenary = (H, L, R, T, chainMass) => {
    const g = 9.81;
    const a = T / (chainMass * g);
    const points = [];
    const numPoints = 100;
    
    for (let i = 0; i <= numPoints; i++) {
      const x = (R / numPoints) * i;
      const y = a * (Math.cosh(x / a) - 1);
      const scaleFactor = H / (a * (Math.cosh(R / a) - 1));
      points.push({ x, y: y * scaleFactor });
    }
    
    return points;
  };

  const renderBuoy = (buoyType, x, y, scaleY, scaleX, color) => {
    const dims = buoyDimensions[buoyType];
    if (!dims) return null;
    
    const [r1Width, r1HeightUnder, r1HeightAbove, r2Width, r2Height] = dims;
    const r1W = (r1Width / 1000) * scaleX;
    const r1HU = (r1HeightUnder / 1000) * scaleY;
    const r1HA = (r1HeightAbove / 1000) * scaleY;
    const r2W = (r2Width / 1000) * scaleX;
    const r2H = (r2Height / 1000) * scaleY;
    
    return (
      <g transform={`translate(${x}, ${y})`}>
        <rect x={-r1W/2} y={0} width={r1W} height={r1HU} 
              fill="#A0A0A0" stroke="#1F2937" strokeWidth={1} />
        <rect x={-r1W/2} y={-r1HA} width={r1W} height={r1HA} 
              fill={color} stroke="#1F2937" strokeWidth={1} />
        <line x1={-r1W/2} y1={0} x2={r1W/2} y2={0} stroke="#000000" strokeWidth={2} />
        <rect x={-r2W/2} y={-r1HA - r2H} width={r2W} height={r2H} 
              fill={color} stroke="#1F2937" strokeWidth={1} />
        <path d={`M ${-r2W/4} ${-r1HA - r2H} L 0 ${-r1HA - r2H - r2W/4} L ${r2W/4} ${-r1HA - r2H} Z`} 
              fill={color} stroke="#1F2937" strokeWidth={1} />
      </g>
    );
  };

  const fullOceansBlue = '#0065B5';
  const fullOceansBlueLight = '#0080E6';
  const fullOceansBlueDark = '#004A85';
  
  const buoyUrls = {
    FLC1200: 'https://fulloceans.com/en/14-flc1200-marine-buoy',
    FLC1500: 'https://fulloceans.com/en/15-flc1500-marine-buoy',
    FLC1800: 'https://fulloceans.com/en/16-flc1800-marine-buoy',
    FLC1800HV: 'https://fulloceans.com/en/16-flc1800-marine-buoy',
    FLC2200: 'https://fulloceans.com/en/17-flc2200-marine-buoy',
    FLC2200HV: 'https://fulloceans.com/en/17-flc2200-marine-buoy',
    FLC2600: 'https://fulloceans.com/en/31-flc2600-navigation-off-shore-buoy',
    FLC3000: 'https://fulloceans.com/en/30-flc3000-marine-buoy'
  };

  const getBuoyUrl = (buoyName) => buoyUrls[buoyName] || '#';

  const BuoyLink = ({ buoyName }) => (
    <a 
      href={getBuoyUrl(buoyName)} 
      target="_blank" 
      rel="noopener noreferrer"
      style={{ color: fullOceansBlue, textDecoration: 'none', fontWeight: 'bold', cursor: 'pointer' }}
      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
    >
      {buoyName.replace('HV', ' HV')}
    </a>
  );
  
  const logoUrl = "./logoflc.jpg";

  const renderCombinedDiagram = () => {
    if (!results1 || !results1.valid) return null;

    const { H: H1, len: len1, radius: radius1, T: T1, chainMass: chainMass1, sinker: sinker1 } = results1;
    const hasResult2 = numBuoys === 2 && results2 && results2.valid;
    const H2 = hasResult2 ? results2.H : 0;
    const len2 = hasResult2 ? results2.len : 0;
    const radius2 = hasResult2 ? results2.radius : 0;
    const T2 = hasResult2 ? results2.T : 0;
    const chainMass2 = hasResult2 ? results2.chainMass : 0;
    const sinker2 = hasResult2 ? results2.sinker : 0;
    
    const width = 800;
    const height = 700;
    const paddingLeft = 60;
    const paddingRight = 60;
    const paddingTop = 150;
    const paddingBottom = 60;
    const graphWidth = width - paddingLeft - paddingRight;
    const graphHeight = height - paddingTop - paddingBottom;
    
    const maxDepth = Math.max(H1, hasResult2 ? H2 : 0);
    const maxWidth = Math.max(radius1, hasResult2 ? radius2 : 0) * 1.3;
    const scaleY = graphHeight / maxDepth;
    const scaleX = graphWidth / maxWidth;
    
    // Configuration 1
    const catenary1 = generateCatenary(H1, len1, radius1, T1, chainMass1);
    const svgPoints1 = catenary1.map(p => ({
      x: paddingLeft + p.x * scaleX,
      y: (height - paddingBottom) - p.y * scaleY
    }));
    const pathString1 = svgPoints1.map((p, i) => 
      `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
    ).join(' ');
    const buoy1X = paddingLeft + radius1 * scaleX;
    const buoy1Y = paddingTop;
    const sinker1X = paddingLeft;
    const sinker1Y = height - paddingBottom;
    
    // Configuration 2
    let svgPoints2 = [];
    let pathString2 = '';
    let buoy2X = 0;
    let buoy2Y = 0;
    let sinker2X = 0;
    let sinker2Y = 0;
    
    if (hasResult2) {
      const catenary2 = generateCatenary(H2, len2, radius2, T2, chainMass2);
      svgPoints2 = catenary2.map(p => ({
        x: paddingLeft + p.x * scaleX,
        y: (height - paddingBottom) - p.y * scaleY
      }));
      pathString2 = svgPoints2.map((p, i) => 
        `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
      ).join(' ');
      buoy2X = paddingLeft + radius2 * scaleX;
      buoy2Y = paddingTop;
      sinker2X = paddingLeft;
      sinker2Y = height - paddingBottom;
    }
    
    const numHorizontalLines = 6;
    const horizontalLines = [];
    for (let i = 0; i <= numHorizontalLines; i++) {
      const depth = (maxDepth / numHorizontalLines) * i;
      const y = paddingTop + (depth / maxDepth) * graphHeight;
      horizontalLines.push({ y, label: depth.toFixed(1) });
    }
    
    const numVerticalLines = 5;
    const verticalLines = [];
    for (let i = 0; i <= numVerticalLines; i++) {
      const dist = (maxWidth / numVerticalLines) * i;
      const x = paddingLeft + (dist * scaleX);
      verticalLines.push({ x, label: dist.toFixed(1) });
    }

    return (
      <>
        <div className="bg-white border-2 border-gray-800 mt-8">
          <div className="bg-gray-800 text-white p-6">
            <h3 className="text-2xl font-bold uppercase">Combined Mooring System Diagram</h3>
          </div>
          
          <div className="p-6">
            <svg width={width} height={height} className="border-2 border-gray-300">
              <defs>
                <linearGradient id="waterGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#E0F2FE', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#0EA5E9', stopOpacity: 0.3 }} />
                </linearGradient>
              </defs>
              
              <rect x={0} y={0} width={width} height={paddingTop} fill="#F0F9FF" />
              <rect x={0} y={paddingTop} width={width} height={height - paddingTop - paddingBottom} fill="url(#waterGradient)" />
              
              {horizontalLines.map((line, i) => (
                <g key={`h-${i}`}>
                  <line x1={paddingLeft} y1={line.y} x2={width - paddingRight} y2={line.y} stroke="#CBD5E1" strokeWidth="1" strokeDasharray="5,5" />
                  <text x={paddingLeft - 10} y={line.y + 5} textAnchor="end" fontSize="12" fill="#475569" fontWeight="bold">{line.label}m</text>
                </g>
              ))}
              
              {verticalLines.map((line, i) => (
                <g key={`v-${i}`}>
                  <line x1={line.x} y1={paddingTop} x2={line.x} y2={height - paddingBottom} stroke="#CBD5E1" strokeWidth="1" strokeDasharray="5,5" />
                  <text x={line.x} y={height - paddingBottom + 20} textAnchor="middle" fontSize="12" fill="#475569" fontWeight="bold">{line.label}m</text>
                </g>
              ))}
              
              <line x1={paddingLeft} y1={height - paddingBottom} x2={width - paddingRight} y2={height - paddingBottom} stroke="#8B4513" strokeWidth="4" />
              <rect x={paddingLeft} y={height - paddingBottom} width={graphWidth} height={paddingBottom} fill="none" />
              
              <line x1={paddingLeft} y1={paddingTop} x2={width - paddingRight} y2={paddingTop} stroke="#0EA5E9" strokeWidth="3" />
              <text x={width - paddingRight - 5} y={paddingTop - 8} textAnchor="end" fontSize="12" fill="#0369A1" fontWeight="bold">Sea Level (0m)</text>
              
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => {
                const waveHeight = env.wave * scaleY;
                const waveLength = 70;
                const startX = paddingLeft + i * waveLength;
                return (
                  <path
                    key={i}
                    d={`M ${startX} ${paddingTop} Q ${startX + waveLength/4} ${paddingTop - waveHeight/2}, ${startX + waveLength/2} ${paddingTop} T ${startX + waveLength} ${paddingTop}`}
                    stroke="#0EA5E9"
                    strokeWidth="2"
                    fill="none"
                  />
                );
              })}
              
              <path d={pathString1} stroke={fullOceansBlue} strokeWidth="4" fill="none" strokeLinecap="round" />
              {svgPoints1.length > 0 && renderBuoy(results1.buoyName, buoy1X, buoy1Y, scaleY, scaleX, fullOceansBlue)}
              <line x1={buoy1X} y1={paddingTop} x2={buoy1X} y2={height - paddingBottom} stroke={fullOceansBlue} strokeWidth="2" strokeDasharray="8,4" opacity="0.4" />
              
              <g>
                <rect x={sinker1X - 12} y={sinker1Y - 8} width="24" height="8" fill="#4B5563" stroke={fullOceansBlue} strokeWidth="2" />
                <text x={sinker1X - 25} y={sinker1Y + 20} textAnchor="end" fontSize="11" fill={fullOceansBlue} fontWeight="bold">Config 1: {sinker1}kg</text>
              </g>
              
              <text x={buoy1X} y={buoy1Y - 80} textAnchor="middle" fontSize="11" fill={fullOceansBlue} fontWeight="bold">{results1.buoyName.replace('HV', ' HV')}</text>
              <text x={buoy1X} y={buoy1Y + 80} textAnchor="middle" fontSize="10" fill={fullOceansBlue} fontWeight="bold">R={radius1}m</text>
              
              {hasResult2 && (
                <>
                  <path d={pathString2} stroke={fullOceansBlueDark} strokeWidth="4" fill="none" strokeLinecap="round" />
                  {svgPoints2.length > 0 && renderBuoy(results2.buoyName, buoy2X, buoy2Y, scaleY, scaleX, fullOceansBlueDark)}
                  <line x1={buoy2X} y1={paddingTop} x2={buoy2X} y2={height - paddingBottom} stroke={fullOceansBlueDark} strokeWidth="2" strokeDasharray="8,4" opacity="0.4" />
                  
                  <g>
                    <rect x={sinker2X - 12} y={sinker2Y - 20} width="24" height="8" fill="#4B5563" stroke={fullOceansBlueDark} strokeWidth="2" />
                    <text x={sinker2X - 25} y={sinker2Y - 25} textAnchor="end" fontSize="11" fill={fullOceansBlueDark} fontWeight="bold">Config 2: {sinker2}kg</text>
                  </g>
                  
                  <text x={buoy2X} y={buoy2Y - 80} textAnchor="middle" fontSize="11" fill={fullOceansBlueDark} fontWeight="bold">{results2.buoyName.replace('HV', ' HV')}</text>
                  <text x={buoy2X} y={buoy2Y + 80} textAnchor="middle" fontSize="10" fill={fullOceansBlueDark} fontWeight="bold">R={radius2}m</text>
                </>
              )}
              
              <text x={width / 2} y={height - 8} textAnchor="middle" fontSize="13" fill="#1F2937" fontWeight="bold">Horizontal Distance (m)</text>
              <text x={15} y={height / 2} textAnchor="middle" fontSize="13" fill="#1F2937" fontWeight="bold" transform={`rotate(-90, 15, ${height / 2})`}>Depth (m)</text>
            </svg>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-800 mt-8">
          <div className="bg-gray-800 text-white p-6">
            <h3 className="text-2xl font-bold uppercase">Diagram Configuration Details</h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-200 border-b-2 border-gray-800">
                  <th className="text-left p-4 font-bold uppercase">Parameter</th>
                  <th className="text-center p-4 font-bold uppercase border-x-2" 
                      style={{ backgroundColor: '#E0F2FE', borderColor: fullOceansBlue }}>
                    Configuration 1
                  </th>
                  {hasResult2 && (
                    <th className="text-center p-4 font-bold uppercase border-r-2" 
                        style={{ backgroundColor: '#DBEAFE', borderColor: fullOceansBlueDark }}>
                      Configuration 2
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b-2 border-gray-200">
                  <td className="p-4 font-bold">Buoy</td>
                  <td className="text-center p-4 border-x-2" 
                      style={{ backgroundColor: '#F0F9FF', borderColor: fullOceansBlue }}>
                    <BuoyLink buoyName={results1.buoyName} />
                  </td>
                  {hasResult2 && (
                    <td className="text-center p-4 border-r-2" 
                        style={{ backgroundColor: '#EFF6FF', borderColor: fullOceansBlueDark }}>
                      <BuoyLink buoyName={results2.buoyName} />
                    </td>
                  )}
                </tr>
                
                <tr className="border-b-2 border-gray-200">
                  <td className="p-4 font-bold">Chain</td>
                  <td className="text-center p-4 border-x-2 text-xs" 
                      style={{ backgroundColor: '#F0F9FF', borderColor: fullOceansBlue }}>
                    {results1.chainName}
                  </td>
                  {hasResult2 && (
                    <td className="text-center p-4 border-r-2 text-xs" 
                        style={{ backgroundColor: '#EFF6FF', borderColor: fullOceansBlueDark }}>
                      {results2.chainName}
                    </td>
                  )}
                </tr>

                <tr className="border-b-2 border-gray-200 bg-yellow-400">
                  <td className="p-4 font-bold uppercase">Chain Length</td>
                  <td className="text-center p-4 border-x-2 font-mono text-lg font-bold" 
                      style={{ backgroundColor: '#BAE6FD', borderColor: fullOceansBlue, color: fullOceansBlueDark }}>
                    {len1} m
                  </td>
                  {hasResult2 && (
                    <td className="text-center p-4 border-r-2 font-mono text-lg font-bold" 
                        style={{ backgroundColor: '#93C5FD', borderColor: fullOceansBlueDark, color: fullOceansBlueDark }}>
                      {len2} m
                    </td>
                  )}
                </tr>

                <tr className="border-b-2 border-gray-200">
                  <td className="p-4 font-bold">Swing Radius</td>
                  <td className="text-center p-4 border-x-2 font-mono font-bold" 
                      style={{ backgroundColor: '#F0F9FF', borderColor: fullOceansBlue }}>
                    {radius1} m
                  </td>
                  {hasResult2 && (
                    <td className="text-center p-4 border-r-2 font-mono font-bold" 
                        style={{ backgroundColor: '#EFF6FF', borderColor: fullOceansBlueDark }}>
                      {radius2} m
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div style={{ backgroundColor: fullOceansBlue }} className="p-8 mb-8 relative">
          <button 
            onClick={() => window.open('./flc-mooring-doc.html', '_blank')}
            className="absolute top-8 right-8 font-bold py-3 px-6 transition-all"
            style={{ backgroundColor: fullOceansBlueLight, color: 'white', border: 'none' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#fff';
              e.currentTarget.style.color = fullOceansBlue;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = fullOceansBlueLight;
              e.currentTarget.style.color = 'white';
            }}>
            USER MANUAL
          </button>
          <div className="flex items-center gap-6">
            <a 
              href="https://www.fulloceans.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity cursor-pointer"
            >
              <img 
                src={logoUrl}
                alt="FullOceans Logo" 
                className="w-24 h-24 bg-white p-2"
              />
            </a>
            <div>
              <h1 className="text-white text-3xl font-bold">Mooring Line Assessment</h1>
              <p className="text-white text-lg mt-1">Calculations based on IALA recommendations</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border-l-4 p-4 mb-8" style={{ borderLeftColor: fullOceansBlue }}>
          <div className="flex items-start gap-3">
            <Info className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: fullOceansBlue }} />
            <p className="text-gray-800 font-medium">Compare up to 2 configurations under the same environmental conditions</p>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 p-6 mb-8">
          <h3 className="font-bold text-gray-800 mb-4 text-xl">Number of Configurations</h3>
          <div className="flex gap-4">
            <label className="flex-1">
              <input type="radio" value="1" checked={numBuoys === 1} onChange={() => setNumBuoys(1)} className="peer sr-only" />
              <div className="cursor-pointer bg-gray-100 peer-checked:bg-gray-600 peer-checked:text-white border-2 border-gray-300 peer-checked:border-gray-600 p-4 text-center transition-colors">
                <span className="font-bold text-lg">1 Buoy</span>
              </div>
            </label>
            <label className="flex-1">
              <input type="radio" value="2" checked={numBuoys === 2} onChange={() => setNumBuoys(2)} className="peer sr-only" />
              <div className="cursor-pointer bg-gray-100 peer-checked:bg-gray-600 peer-checked:text-white border-2 border-gray-300 peer-checked:border-gray-600 p-4 text-center transition-colors">
                <span className="font-bold text-lg">2 Buoys</span>
              </div>
            </label>
          </div>
        </div>

        <div className="mb-8">
          <h3 className="font-bold text-gray-800 mb-6 text-xl">Buoy Configurations</h3>
          <div className={`grid gap-6 ${numBuoys === 2 ? 'grid-cols-2' : 'grid-cols-1 max-w-2xl mx-auto'}`}>
            <div className="bg-white border-2" style={{ borderColor: fullOceansBlue }}>
              <div className="text-white p-4" style={{ backgroundColor: fullOceansBlue }}>
                <h3 className="font-bold text-xl">Configuration 1</h3>
              </div>
              
              <div className="p-6 space-y-6">
                <div>
                  <label className="block font-bold text-gray-800 mb-3 text-sm uppercase tracking-wide">Buoy</label>
                  <select value={buoy1} onChange={(e) => changeBuoy1(e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 font-bold text-gray-800 focus:outline-none transition-colors"
                    style={{ borderColor: fullOceansBlue }}>
                    {Object.keys(buoys).map(k => (
                      <option key={k} value={k}>{k.replace('HV', ' HV')}</option>
                    ))}
                  </select>
                  
                  <button onClick={() => setShowBuoy1Details(!showBuoy1Details)}
                    className="w-full mt-3 py-3 text-white font-bold flex items-center justify-center gap-2 transition-colors"
                    style={{ backgroundColor: fullOceansBlue }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = fullOceansBlueLight}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = fullOceansBlue}>
                    {showBuoy1Details ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    {showBuoy1Details ? 'Hide' : 'Show'} Specifications
                  </button>
                  
                  {showBuoy1Details && (
                    <div className="grid grid-cols-2 gap-3 mt-4 p-4 bg-gray-50 border-2 border-gray-200">
                      <div>
                        <label className="block text-xs font-bold text-gray-600 mb-2 uppercase">Mass (kg)</label>
                        <input type="number" value={data1.mass} readOnly className="w-full p-2 border-2 border-gray-300 text-sm bg-white font-bold" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-600 mb-2 uppercase">Volume (m³)</label>
                        <input type="number" value={data1.vol} readOnly className="w-full p-2 border-2 border-gray-300 text-sm bg-white font-bold" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-600 mb-2 uppercase">Above Water Area (m²)</label>
                        <input type="number" value={data1.area1} readOnly className="w-full p-2 border-2 border-gray-300 text-sm bg-white font-bold" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-600 mb-2 uppercase">Underwater Area (m²)</label>
                        <input type="number" value={data1.area2} readOnly className="w-full p-2 border-2 border-gray-300 text-sm bg-white font-bold" />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block font-bold text-gray-800 mb-3 text-sm uppercase tracking-wide">Chain</label>
                  <select value={chain1} onChange={(e) => changeChain1(e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 text-sm font-medium text-gray-800 focus:outline-none transition-colors"
                    style={{ borderColor: fullOceansBlue }}>
                    {(compat[buoy1] || Object.keys(chains)).map(k => (
                      <option key={k} value={k}>{chains[k].name}</option>
                    ))}
                  </select>
                  
                  <button onClick={() => setShowChain1Details(!showChain1Details)}
                    className="w-full mt-3 py-3 text-white font-bold flex items-center justify-center gap-2 transition-colors"
                    style={{ backgroundColor: fullOceansBlue }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = fullOceansBlueLight}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = fullOceansBlue}>
                    {showChain1Details ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    {showChain1Details ? 'Hide' : 'Show'} Specifications
                  </button>
                  
                  {showChain1Details && (
                    <div className="space-y-3 mt-4 p-4 bg-gray-50 border-2 border-gray-200">
                      <div>
                        <label className="block text-xs font-bold text-gray-600 mb-2 uppercase">Diameter (mm)</label>
                        <input type="number" value={data1.diam} readOnly className="w-full p-2 border-2 border-gray-300 text-sm bg-white font-bold" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-600 mb-2 uppercase">Mass (kg/m)</label>
                        <input type="number" value={data1.chainMass} readOnly className="w-full p-2 border-2 border-gray-300 text-sm bg-white font-bold" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-600 mb-2 uppercase">Load (N)</label>
                        <input type="number" value={data1.proof} readOnly className="w-full p-2 border-2 border-gray-300 text-sm bg-white font-bold" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {numBuoys === 2 && (
              <div className="bg-white border-2" style={{ borderColor: fullOceansBlueDark }}>
                <div className="text-white p-4" style={{ backgroundColor: fullOceansBlueDark }}>
                  <h3 className="font-bold text-xl">Configuration 2</h3>
                </div>
                
                <div className="p-6 space-y-6">
                  <div>
                    <label className="block font-bold text-gray-800 mb-3 text-sm uppercase tracking-wide">Buoy</label>
                    <select value={buoy2} onChange={(e) => changeBuoy2(e.target.value)}
                      className="w-full p-3 border-2 border-gray-300 font-bold text-gray-800 focus:outline-none transition-colors"
                      style={{ borderColor: fullOceansBlueDark }}>
                      {Object.keys(buoys).map(k => (
                        <option key={k} value={k}>{k.replace('HV', ' HV')}</option>
                      ))}
                    </select>
                    
                    <button onClick={() => setShowBuoy2Details(!showBuoy2Details)}
                      className="w-full mt-3 py-3 text-white font-bold flex items-center justify-center gap-2 transition-colors"
                      style={{ backgroundColor: fullOceansBlueDark }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = fullOceansBlue}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = fullOceansBlueDark}>
                      {showBuoy2Details ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      {showBuoy2Details ? 'Hide' : 'Show'} Specifications
                    </button>
                    
                    {showBuoy2Details && (
                      <div className="grid grid-cols-2 gap-3 mt-4 p-4 bg-gray-50 border-2 border-gray-200">
                        <div>
                          <label className="block text-xs font-bold text-gray-600 mb-2 uppercase">Mass (kg)</label>
                          <input type="number" value={data2.mass} readOnly className="w-full p-2 border-2 border-gray-300 text-sm bg-white font-bold" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-600 mb-2 uppercase">Volume (m³)</label>
                          <input type="number" value={data2.vol} readOnly className="w-full p-2 border-2 border-gray-300 text-sm bg-white font-bold" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-600 mb-2 uppercase">Above Water Area (m²)</label>
                          <input type="number" value={data2.area1} readOnly className="w-full p-2 border-2 border-gray-300 text-sm bg-white font-bold" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-600 mb-2 uppercase">Underwater Area (m²)</label>
                          <input type="number" value={data2.area2} readOnly className="w-full p-2 border-2 border-gray-300 text-sm bg-white font-bold" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block font-bold text-gray-800 mb-3 text-sm uppercase tracking-wide">Chain</label>
                    <select value={chain2} onChange={(e) => changeChain2(e.target.value)}
                      className="w-full p-3 border-2 border-gray-300 text-sm font-medium text-gray-800 focus:outline-none transition-colors"
                      style={{ borderColor: fullOceansBlueDark }}>
                      {(compat[buoy2] || Object.keys(chains)).map(k => (
                        <option key={k} value={k}>{chains[k].name}</option>
                      ))}
                    </select>
                    
                    <button onClick={() => setShowChain2Details(!showChain2Details)}
                      className="w-full mt-3 py-3 text-white font-bold flex items-center justify-center gap-2 transition-colors"
                      style={{ backgroundColor: fullOceansBlueDark }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = fullOceansBlue}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = fullOceansBlueDark}>
                      {showChain2Details ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      {showChain2Details ? 'Hide' : 'Show'} Specifications
                    </button>
                    
                    {showChain2Details && (
                      <div className="space-y-3 mt-4 p-4 bg-gray-50 border-2 border-gray-200">
                        <div>
                          <label className="block text-xs font-bold text-gray-600 mb-2 uppercase">Diameter (mm)</label>
                          <input type="number" value={data2.diam} readOnly className="w-full p-2 border-2 border-gray-300 text-sm bg-white font-bold" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-600 mb-2 uppercase">Mass (kg/m)</label>
                          <input type="number" value={data2.chainMass} readOnly className="w-full p-2 border-2 border-gray-300 text-sm bg-white font-bold" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-600 mb-2 uppercase">Load (N)</label>
                          <input type="number" value={data2.proof} readOnly className="w-full p-2 border-2 border-gray-300 text-sm bg-white font-bold" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 p-6 mb-8">
          <h3 className="font-bold text-gray-800 mb-6 text-xl">Environmental Conditions</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">Nominal Depth (m)</label>
              <input type="number" step="0.1" value={env.depth} onChange={(e) => updateEnv('depth', e.target.value)}
                className="w-full p-3 border-2 border-gray-300 font-bold focus:outline-none transition-colors"
                style={{ borderColor: fullOceansBlue }} />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">Tidal Range (m)</label>
              <input type="number" step="0.1" value={env.tide} onChange={(e) => updateEnv('tide', e.target.value)}
                className="w-full p-3 border-2 border-gray-300 font-bold focus:outline-none transition-colors"
                style={{ borderColor: fullOceansBlue }} />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">Max Wave Height (m)</label>
              <input type="number" step="0.1" value={env.wave} onChange={(e) => updateEnv('wave', e.target.value)}
                className="w-full p-3 border-2 border-gray-300 font-bold focus:outline-none transition-colors"
                style={{ borderColor: fullOceansBlue }} />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">Period (s)</label>
              <input type="number" step="0.1" value={env.period} onChange={(e) => updateEnv('period', e.target.value)}
                className="w-full p-3 border-2 border-gray-300 font-bold focus:outline-none transition-colors"
                style={{ borderColor: fullOceansBlue }} />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">Max Wind (m/s)</label>
              <input type="number" step="0.1" value={env.wind} onChange={(e) => updateEnv('wind', e.target.value)}
                className="w-full p-3 border-2 border-gray-300 font-bold focus:outline-none transition-colors"
                style={{ borderColor: fullOceansBlue }} />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">Current (m/s)</label>
              <input type="number" step="0.1" value={env.current} onChange={(e) => updateEnv('current', e.target.value)}
                className="w-full p-3 border-2 border-gray-300 font-bold focus:outline-none transition-colors"
                style={{ borderColor: fullOceansBlue }} />
            </div>
          </div>
        </div>

        <button onClick={calcAll}
          className="w-full text-white font-bold py-5 px-6 flex items-center justify-center gap-3 mb-8 transition-colors"
          style={{ backgroundColor: fullOceansBlue }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = fullOceansBlueLight}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = fullOceansBlue}>
          <Calculator className="w-7 h-7" />
          <span className="text-2xl">CALCULATE MOORINGS</span>
        </button>

        {results1 && (
          <>
          <div className="bg-white border-2 border-gray-800 mb-8">
            <div className="bg-gray-800 text-white p-6">
              <h3 className="text-2xl font-bold uppercase">Calculation Results</h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-200 border-b-2 border-gray-800">
                    <th className="text-left p-4 font-bold uppercase">Parameter</th>
                    <th className="text-center p-4 font-bold uppercase border-x-2" 
                        style={{ backgroundColor: '#E0F2FE', borderColor: fullOceansBlue }}>
                      Configuration 1
                    </th>
                    {numBuoys === 2 && results2 && (
                      <th className="text-center p-4 font-bold uppercase border-r-2" 
                          style={{ backgroundColor: '#DBEAFE', borderColor: fullOceansBlueDark }}>
                        Configuration 2
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <td className="p-3 font-bold text-gray-800 uppercase">Parameters</td>
                    <td className="border-x-2" style={{ backgroundColor: '#E0F2FE', borderColor: fullOceansBlue }}></td>
                    {numBuoys === 2 && results2 && (
                      <td className="border-r-2" style={{ backgroundColor: '#DBEAFE', borderColor: fullOceansBlueDark }}></td>
                    )}
                  </tr>

                  <tr className="border-b-2 border-gray-200">
                    <td className="p-4 font-bold">Buoy</td>
                    <td className="text-center p-4 border-x-2" 
                        style={{ backgroundColor: '#F0F9FF', borderColor: fullOceansBlue }}>
                      <BuoyLink buoyName={results1.buoyName} />
                    </td>
                    {numBuoys === 2 && results2 && (
                      <td className="text-center p-4 border-r-2" 
                          style={{ backgroundColor: '#EFF6FF', borderColor: fullOceansBlueDark }}>
                        <BuoyLink buoyName={results2.buoyName} />
                      </td>
                    )}
                  </tr>
                  
                  <tr className="border-b-2 border-gray-200">
                    <td className="p-4 font-bold">Chain</td>
                    <td className="text-center p-4 border-x-2 text-xs" 
                        style={{ backgroundColor: '#F0F9FF', borderColor: fullOceansBlue }}>
                      {results1.chainName}
                    </td>
                    {numBuoys === 2 && results2 && (
                      <td className="text-center p-4 border-r-2 text-xs" 
                          style={{ backgroundColor: '#EFF6FF', borderColor: fullOceansBlueDark }}>
                        {results2.chainName}
                      </td>
                    )}
                  </tr>

                  {!results1.valid && (
                    <tr className="border-b-2 border-gray-200">
                      <td className="p-4 font-bold">Status</td>
                      <td className="text-center p-4 border-x-2" 
                          style={{ backgroundColor: '#F0F9FF', borderColor: fullOceansBlue }}>
                        <div className="inline-flex items-center gap-2 bg-red-500 text-white px-4 py-2">
                          <AlertTriangle className="w-5 h-5" />
                          <span className="font-bold text-sm uppercase">Unsuitable Combination</span>
                        </div>
                      </td>
                      {numBuoys === 2 && results2 && (
                        <td className="border-r-2" style={{ backgroundColor: '#EFF6FF', borderColor: fullOceansBlueDark }}></td>
                      )}
                    </tr>
                  )}

                  {numBuoys === 2 && results2 && !results2.valid && (
                    <tr className="border-b-2 border-gray-200">
                      <td className="p-4 font-bold">Status</td>
                      <td className="border-x-2" style={{ backgroundColor: '#F0F9FF', borderColor: fullOceansBlue }}></td>
                      <td className="text-center p-4 border-r-2" 
                          style={{ backgroundColor: '#EFF6FF', borderColor: fullOceansBlueDark }}>
                        <div className="inline-flex items-center gap-2 bg-red-500 text-white px-4 py-2">
                          <AlertTriangle className="w-5 h-5" />
                          <span className="font-bold text-sm uppercase">Unsuitable Combination</span>
                        </div>
                      </td>
                    </tr>
                  )}

                  {(results1.valid || (results2 && results2.valid)) && (
                    <>
                      <tr className="bg-gray-100 border-b-2 border-gray-300">
                        <td colSpan={numBuoys === 2 && results2 ? 3 : 2} className="p-3 font-bold text-gray-800 uppercase">Dimensions</td>
                      </tr>

                      <tr className="border-b-2 border-gray-300 bg-yellow-400">
                        <td className="p-4 font-bold uppercase">Chain Length</td>
                        <td className="text-center p-4 border-x-2 font-mono text-2xl font-bold" 
                            style={{ backgroundColor: '#BAE6FD', borderColor: fullOceansBlue, color: fullOceansBlueDark }}>
                          {results1.valid ? results1.len + ' m' : '-'}
                        </td>
                        {numBuoys === 2 && results2 && (
                          <td className="text-center p-4 border-r-2 font-mono text-2xl font-bold" 
                              style={{ backgroundColor: '#93C5FD', borderColor: fullOceansBlueDark, color: fullOceansBlueDark }}>
                            {results2.valid ? results2.len + ' m' : '-'}
                          </td>
                        )}
                      </tr>

                      <tr className="border-b-2 border-gray-200">
                        <td className="p-4 font-bold">Swing Radius</td>
                        <td className="text-center p-4 border-x-2 font-mono font-bold" 
                            style={{ backgroundColor: '#F0F9FF', borderColor: fullOceansBlue }}>
                          {results1.valid ? results1.radius + ' m' : '-'}
                        </td>
                        {numBuoys === 2 && results2 && (
                          <td className="text-center p-4 border-r-2 font-mono font-bold" 
                              style={{ backgroundColor: '#EFF6FF', borderColor: fullOceansBlueDark }}>
                            {results2.valid ? results2.radius + ' m' : '-'}
                          </td>
                        )}
                      </tr>

                      <tr className="border-b-2 border-gray-200">
                        <td className="p-4 font-bold">Residual Buoyancy</td>
                        <td className="text-center p-4 border-x-2 font-mono font-bold" 
                            style={{ backgroundColor: '#F0F9FF', borderColor: fullOceansBlue }}>
                          {results1.valid ? `${results1.resB} m³ (${results1.resP}%)` : '-'}
                        </td>
                        {numBuoys === 2 && results2 && (
                          <td className="text-center p-4 border-r-2 font-mono font-bold" 
                              style={{ backgroundColor: '#EFF6FF', borderColor: fullOceansBlueDark }}>
                            {results2.valid ? `${results2.resB} m³ (${results2.resP}%)` : '-'}
                          </td>
                        )}
                      </tr>

                      <tr className="border-b-2 border-gray-200">
                        <td className="p-4 font-bold">Recommended Sinker</td>
                        <td className="text-center p-4 border-x-2 font-mono font-bold" 
                            style={{ backgroundColor: '#F0F9FF', borderColor: fullOceansBlue }}>
                          {results1.valid ? results1.sinker + ' kg' : '-'}
                        </td>
                        {numBuoys === 2 && results2 && (
                          <td className="text-center p-4 border-r-2 font-mono font-bold" 
                              style={{ backgroundColor: '#EFF6FF', borderColor: fullOceansBlueDark }}>
                            {results2.valid ? results2.sinker + ' kg' : '-'}
                          </td>
                        )}
                      </tr>

                      <tr className="bg-gray-100 border-b-2 border-gray-300">
                        <td colSpan={numBuoys === 2 && results2 ? 3 : 2} className="p-3 font-bold text-gray-800 uppercase">Validation</td>
                      </tr>

                      <tr className="border-b-2 border-gray-200">
                        <td className="p-4 font-bold">Required Load</td>
                        <td className="text-center p-4 border-x-2 font-mono font-bold" 
                            style={{ backgroundColor: '#F0F9FF', borderColor: fullOceansBlue }}>
                          {results1.valid ? results1.req + ' kN' : '-'}
                        </td>
                        {numBuoys === 2 && results2 && (
                          <td className="text-center p-4 border-r-2 font-mono font-bold" 
                              style={{ backgroundColor: '#EFF6FF', borderColor: fullOceansBlueDark }}>
                            {results2.valid ? results2.req + ' kN' : '-'}
                          </td>
                        )}
                      </tr>

                      <tr className="border-b-2 border-gray-300">
                        <td className="p-4 font-bold uppercase">Chain Validation</td>
                        <td className="text-center p-4 border-x-2" 
                            style={{ backgroundColor: '#F0F9FF', borderColor: fullOceansBlue }}>
                          {results1.valid ? (
                            <div className={`inline-flex items-center gap-2 px-6 py-3 font-bold uppercase ${results1.ok ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                              <div className={`w-4 h-4 ${results1.ok ? 'bg-white' : 'bg-white'}`}></div>
                              {results1.ok ? 'VALID' : 'INVALID'}
                            </div>
                          ) : '-'}
                        </td>
                        {numBuoys === 2 && results2 && (
                          <td className="text-center p-4 border-r-2" 
                              style={{ backgroundColor: '#EFF6FF', borderColor: fullOceansBlueDark }}>
                            {results2.valid ? (
                              <div className={`inline-flex items-center gap-2 px-6 py-3 font-bold uppercase ${results2.ok ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                                <div className={`w-4 h-4 ${results2.ok ? 'bg-white' : 'bg-white'}`}></div>
                                {results2.ok ? 'VALID' : 'INVALID'}
                              </div>
                            ) : '-'}
                          </td>
                        )}
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {renderCombinedDiagram()}

          <div className="bg-yellow-50 border-l-4 p-4 mt-8" style={{ borderLeftColor: '#F59E0B' }}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#D97706' }} />
              <p className="text-gray-800 font-medium text-sm leading-relaxed">
                This tool provides engineering calculations based on standard hydrodynamic principles and IALA guidelines. All results should be reviewed by qualified marine engineers before implementation. The software provides estimates and recommendations; actual field conditions may vary. Users assume full responsibility for validation and application of results to specific projects. Always consult with local maritime authorities and qualified professionals for mooring installations.
              </p>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
